import { useRef } from 'react';
import { mergeProps, useFocusRing, useRadio, VisuallyHidden } from 'react-aria';
import { type RadioGroupState } from 'react-stately';

import { clsx } from '../../lib/clsx';
import { TextMedium } from '../common/text/TextMedium.tsx';
import { RadioIndicator } from '../forms/radio/RadioIndicator';

import { cssClass } from '../../theme/cssClass';
import styles from './AppearanceOption.module.css';

export type Appearance = 'light' | 'dark';

interface AppearanceOptionProps {
  value: Appearance;
  label: string;
  state: RadioGroupState;
}

/**
 * Radio card in the appearance dialog, with a small preview of the app in that appearance.
 */
export function AppearanceOption(props: AppearanceOptionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const radio = useRadio({ value: props.value, children: props.label }, props.state, inputRef);
  const focusRing = useFocusRing();

  // The preview gets its own theme class, so it shows that appearance's colors in either theme.
  const previewThemeClassName = props.value === 'dark' ? cssClass.darkTheme : cssClass.lightTheme;

  return (
    // labelProps cancel the label's native click. Chromium and Safari forward that click to the
    // input as one that react-aria takes for a screen reader, which shows the focus ring.
    <label
      {...radio.labelProps}
      className={styles.root}
      data-is-selected={radio.isSelected}
      data-is-focus-visible={focusRing.isFocusVisible}
    >
      <VisuallyHidden>
        <input {...mergeProps(radio.inputProps, focusRing.focusProps)} ref={inputRef} />
      </VisuallyHidden>

      <span
        className={clsx(styles.preview, previewThemeClassName)}
        data-appearance={props.value}
        aria-hidden="true"
      >
        <span className={styles.previewCard}>
          <span className={styles.previewIcon} />

          <span className={styles.previewLines}>
            <span className={styles.previewTitle} />
            <span className={styles.previewSubtitle} />
          </span>
        </span>
      </span>

      <TextMedium className={styles.label}>{props.label}</TextMedium>

      <RadioIndicator isSelected={radio.isSelected} isFocusVisible={focusRing.isFocusVisible} />
    </label>
  );
}
